import { Button, Modal, Space, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import client from '@/lib/feathers';
import type { UserGroup, UserRouter } from '@/types/database/userGroup';
import type UserGroupRoute from '@/types/database/userGroupRoute';

import AuthSetting from './AuthSetting';
import type { TableDataSource } from './types';

const { confirm } = Modal;

export const defaultAuths = ['superAdmin', 'admin', 'app', 'guest', 'user'];

const showDeleteConfirm = (id: string, updateUserGroups: () => void) => {
  confirm({
    title: '確定刪除該權限嗎?',
    icon: <ExclamationCircleOutlined />,
    content: '確定刪除請按確定鍵',
    onOk: async () => {
      try {
        await client.service('userGroup').remove(id);
        updateUserGroups();
        message.success('刪除權限成功!');
      } catch (error) {
        message.error('刪除權限失敗!');
      }
    },
  });
};

const downloadQRCode = async (auth: string) => {
  return new Promise((resolve, reject) => {
    try {
      resolve(
        (window.location.href = `/ezcon/api/backendEnv/proVersion/downloadQrCode/adminMember?auth=${auth}&token=${localStorage.getItem(
          'feathers-jwt'
        )}`)
      );
      message.success('下載QR Code成功!');
    } catch (error) {
      reject(error);
      message.error('下載QR Code失敗!');
    }
  });
};

export const getColumns = (args: {
  auth: string;
  userGroups: UserGroup[];
  updateUserGroups: () => void;
}) => {
  const { auth, userGroups, updateUserGroups } = args;
  const columns: ColumnsType<TableDataSource> = [
    {
      title: '服務類型',
      dataIndex: 'serviceName',
    },
    {
      title: '角色設定',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: '頁面名稱',
      dataIndex: 'userRouter',
    },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 200,
      fixed: 'right',
      render: (_value, record) => {
        return (
          <Space>
            <Button type="primary" onClick={() => downloadQRCode(record.name)}>
              下載QR code
            </Button>
            <AuthSetting
              type="edit"
              userGroups={userGroups}
              record={record}
              updateUserGroups={updateUserGroups}
            />
            {defaultAuths.includes(record.name) === false && (
              <Button
                type="primary"
                danger
                onClick={() => showDeleteConfirm(record._id, updateUserGroups)}
              >
                刪除
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const order = {
    title: '位階',
    dataIndex: 'order',
  };

  if (auth === 'superAdmin') {
    columns.splice(1, 0, order);
  }

  return columns;
};

export const getTableData = (
  userGroups: UserGroup[],
  selectedService: string
) => {
  let dataSource = userGroups.map((userGroup) => {
    const { _id, serviceName, order, name, userRouter } = userGroup;
    const userRouters = userRouter.map((route, index) => (
      <div key={index}>{`功能:${route.name}`}</div>
    ));
    return {
      _id,
      order,
      name,
      serviceName: serviceName ?? '',
      userRouter: userRouters,
    };
  });

  if (selectedService) {
    dataSource = dataSource.filter(
      (userGroup) => userGroup.serviceName === selectedService
    );
  }

  return dataSource;
};

export const getServiceOptions = (userGroups: UserGroup[]) => {
  const result = userGroups.reduce(
    (options: { label: string; value: string }[], userGroup) => {
      const isIncluded = options.some(
        (option) => option.value === userGroup.serviceName
      );
      if (isIncluded === false && userGroup.serviceName) {
        options.push({
          label: userGroup.serviceName,
          value: userGroup.serviceName,
        });
      }
      return options;
    },
    []
  );
  return result;
};

export const getTreeData = (
  userGroupRoutes: (UserGroupRoute | UserRouter)[]
) => {
  const data = userGroupRoutes.reduce((treeData: DataNode[], route) => {
    const node: DataNode = {
      title: route.name,
      key: route.name,
      children: [],
    };

    if (route.children) {
      node.children = route.children.map((childRoute) => ({
        title: childRoute.name,
        key: childRoute.name,
      }));
    }

    const isIncludedNode = treeData.some((node) => {
      return node.title === route.name;
    });

    if (isIncludedNode === false) {
      treeData.push(node);
    }

    return treeData;
  }, []);

  return data;
};

const getParentRoute = (
  userGroupRoutes: UserGroupRoute[],
  routeName: string
) => {
  const foundParentRoute = userGroupRoutes.find((parentRoute) => {
    const isFoundChild = parentRoute.children.some(
      (childRoute) => childRoute.name === routeName
    );
    return isFoundChild;
  });
  return foundParentRoute;
};

export const getUserRouters = (
  userGroupRoutes: UserGroupRoute[],
  checkedKeys: string[]
) => {
  const userRouters = checkedKeys.reduce(
    (
      routes: Pick<
        UserGroupRoute,
        'name' | 'path' | 'order' | 'icon' | 'children'
      >[],
      routeName
    ) => {
      // 先看子還是父
      const isParentRoute = userGroupRoutes.some(
        (parentRoute) => parentRoute.name === routeName
      );
      const foundParentRoute = getParentRoute(userGroupRoutes, routeName);
      // 若為子則需獲得其父
      if (isParentRoute === false && foundParentRoute) {
        // 看是否其父已在routes中
        const isIncludedParentRoute = routes.some(
          (route) => route.name === foundParentRoute.name
        );
        // 若無，則建立新父並在其父的children加上該子
        if (isIncludedParentRoute === false) {
          const { name, path, order, icon } = foundParentRoute;
          const childRoute = foundParentRoute.children.find(
            (child) => child.name === routeName
          );
          if (childRoute) {
            routes.push({
              name,
              path,
              order,
              icon,
              children: [childRoute],
            });
          }
        }
        // 若有，則在該父的children裡添加該子，並更新routes
        if (isIncludedParentRoute) {
          const newRoutes = routes.map((route) => {
            if (route.name === foundParentRoute.name) {
              const childRoute = foundParentRoute.children.find(
                (child) => child.name === routeName
              );
              return {
                ...route,
                children: [...route.children, childRoute],
              };
            }
            return route;
          });
          return newRoutes;
        }
      }
      // 若為父，檢查是否routes已有該父
      if (isParentRoute && foundParentRoute) {
        const isIncludedParentRoute = routes.some(
          (route) => route.name === foundParentRoute.name
        );
        // 若無該父，則建立新父，children為空array
        if (isIncludedParentRoute === false) {
          const { name, path, order, icon } = foundParentRoute;
          const newParentRoute = {
            name,
            path,
            order,
            icon,
            children: [],
          };
          routes.push(newParentRoute);
        }
        // // 若已有該父，則跳過此回
      }
      return routes;
    },
    []
  );
  return userRouters;
};
